import cx from 'classnames';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, memo, useEffect, useRef, useState } from 'react';

import useLoggedUser from '../../../../hooks/use-logged-user';
import { ADVANCED_VIEW_ACTIVE } from '../../../../static/cookies';
import { useAppContext } from '../../../providers/app-context';
import BCItem from './breadcrumb-item';
import classes from './breadcrumbs.module.css';

const Breadcrumbs: FunctionComponent<BreadCrumbsProps> = memo(
  ({ items = [], currentPage }) => {
    const navRef = useRef();
    const currentPageRef = useRef();
    const { t } = useTranslation();
    const { locale = 'en', route } = useRouter();
    const user = useLoggedUser();
    const { tenantConfig } = useAppContext();

    const isPrivateArea = route === '/private-area';
    const isSb = tenantConfig?.id === 5;

    const [rootHref, setRootHref] = useState(isSb ? `/${locale}/sb` : `/${locale}`);
    const [displayItems, setDisplayItems] = useState(() => {
      if (isSb) return items.filter((item) => item.text !== t('common:partscatalog'));
      return items;
    });

    useEffect(() => {
      const isAdvanced = user?.staff?.is_pro;
      if (!isSb) {
        if (isAdvanced) {
          setRootHref(`/${locale}/pro/v2`);
          setDisplayItems(items.filter((item) => item.text !== t('common:partscatalog')));
        } else {
          setRootHref(`/${locale}`);
          setDisplayItems(items);
        }
      }
    }, [user, isSb, locale, items, t]);

    useEffect(() => {
      const breadcrumbsID = document.getElementById('scroll_breadcrums');
      if (breadcrumbsID && navRef.current?.scrollWidth) {
        breadcrumbsID.scrollLeft += navRef.current?.scrollWidth;
      }
    }, []);

    return (
      <div
        className={cx(
          [classes.breadcrumbs],
          'flex',
          'flex-col',
          isPrivateArea && classes.isPrivateArea,
          'bg-neutral-10 w-full'
        )}
        id="breadcrumbs"
      >
        <div className={cx('flex w-full',
          'container',
          'mx-auto',)}>
          <nav
            ref={navRef}
            className={cx('flex w-full',
              'container',
              'lg:mx-auto',
              'px-4',
              'inline-block',
              'items-center',
              'overflow-y-auto'
            )}
            id="scroll_breadcrums"
          >
            <ol data-cy="breadcrums" className={cx('shrink-0')}>
              <li className={cx('inline-block', 'mr-3', 'pt-2', 'pb-1')}>
                <BCItem href={rootHref} text={t('common:home')} />
              </li>
              {displayItems
                .filter(
                  ({ text, as, href }) =>
                    !!text &&
                    (typeof href === 'string' || !!as || !!href.pathname)
                )
                .map(({ text, ...linkProps }) => {
                  return (
                    <li
                      key={text}
                      className={cx('inline-block', 'mr-3', 'pt-2', 'pb-1')}
                    >
                      <BCItem {...linkProps} text={text} />
                    </li>
                  );
                })}
            </ol>
            <span
              ref={currentPageRef}
              key="last"
              className={cx(
                'flex',
                'items-center',
                'shrink-0',
                'whitespace-no-wrap',
                'pt-3',
                'pb-2',
                'text-sm',
                'font-bold'
              )}
            >
              {currentPage}
            </span>
          </nav>
        </div>
      </div>
    );
  }
);

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
